import { PlatformLocation } from '@angular/common';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { InstrumentationService } from '../instrumentation/instrumentation.service';
import { InstrumentedHttp, InstrumentedHttpDataResponse } from '../instrumentation/instrumented-http.service';
import { FileItem, FileType } from '../models/file-item.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { AccessTokenConsumer, AccessTokenService } from '../services/access-token.service';
import { OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { SchedulerService } from '../services/scheduler.service';
import { XhrService } from '../services/xhr.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { GuidGenerator } from '../utilities/guid-generator.utility';
import { MruConfigService } from './mru-config.service';

export abstract class MruBaseService extends XhrService {
    private static readonly localeSpecifier = 'rs=';
    private static readonly sizeSpecifier = 'show=';
    private static readonly appSpecifier = 'apps=';

    protected serviceBaseUrl: string;
    protected parameters: string;

    private readonly supportedApps: Array<string>;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        mruConfigService: MruConfigService,
        platformLocation: PlatformLocation,
        protected globalErrorHandler: OfficeHomePageErrorHandler,
        protected readonly startupConfigService: ClientStartupConfigService,
        mruPath: string,
        apps: Array<string>,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.MruPlusPlus, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        const sharedContext = this.contextService.sharedContext;
        const mruConfig = mruConfigService.config;
        this.supportedApps = apps;
        this.serviceBaseUrl = this.inMockMode ? `${sharedContext.officeHomeApiRoot}/${mruPath}` : `${mruConfig.mruServiceRoot}/${mruPath}`;
    }

    protected requestParams(size: number, requestedTypes?: Set<FileType>) {
        const fileTypes = (requestedTypes && requestedTypes.size) ? this.supportedApps.filter(type => requestedTypes.has(FileItem.fileTypeNameToFileType(type))) : this.supportedApps;
        return `${MruBaseService.appSpecifier}${fileTypes.join(',')}&${MruBaseService.sizeSpecifier}${size}&${MruBaseService.localeSpecifier}${this.contextService.sharedContext.officeMarket}`;
    }

    protected getNewRequestConfig(hasBody: boolean): HttpRequestOptions {
        return { headers: this.requestHeaders(hasBody, GuidGenerator.createGuid()) };
    }

    protected getRequestIdFromResponse(response: InstrumentedHttpDataResponse<any>): string {
        return super.getRequestIdFromResponse(response, 'X-CorrelationId');
    }

    private requestHeaders(hasBody: boolean, requestId: string): Map<string, string | Array<string>> {
        const startupContext = this.startupConfigService.config.sharedClientStartupContext;
        const headers = new Map([
            ['X-Office-Platform', 'Web'],
            ['X-Office-Application', this.contextService.sharedContext.iceApplicationId.toString()],
            ['X-Office-Version', startupContext.buildId],
            ['X-CorrelationId', requestId]
        ]);

        if (hasBody) {
            headers.set('Content-Type', 'application/json');
        }

        return headers;
    }
}
